import React from 'react';

/**
 * Msg #977 — Scene Recap modal.
 * User taps 📼 → we POST /api/scene/recap and render a Chronicler summary
 * of the last N lines. Optional "Save to Memory" persists to
 * db.scene_memories for future reference.
 */
export default function SceneRecapModal({ open, onClose, apiBase, room, username }) {
  const [busy, setBusy] = React.useState(false);
  const [summary, setSummary] = React.useState('');
  const [err, setErr] = React.useState('');
  const [saved, setSaved] = React.useState(false);
  const [lineCount, setLineCount] = React.useState(30);

  React.useEffect(() => {
    if (!open) { setSummary(''); setErr(''); setSaved(false); return; }
  }, [open]);

  const generate = React.useCallback(async () => {
    if (!apiBase || !room) return;
    setBusy(true); setErr(''); setSaved(false);
    try {
      const r = await fetch(`${apiBase}/scene/recap`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requester: username, room, lines: lineCount, save: false }),
      });
      const d = await r.json();
      if (!r.ok) { setErr(d.detail || 'Recap failed.'); return; }
      setSummary(d.summary || '');
    } catch (e) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }, [apiBase, room, username, lineCount]);

  const save = async () => {
    if (!summary) return;
    setBusy(true);
    try {
      const r = await fetch(`${apiBase}/scene/recap`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requester: username, room, lines: lineCount, save: true,
                               label: `${room} — ${new Date().toLocaleString()}` }),
      });
      const d = await r.json();
      if (r.ok) { setSaved(true); setSummary(d.summary || summary); }
    } finally { setBusy(false); }
  };

  React.useEffect(() => { if (open) generate(); }, [open, generate]);

  if (!open) return null;
  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000,
               background: 'rgba(20,40,20,0.55)', display: 'flex',
               alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
      data-testid="scene-recap-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="room-themed-panel"
        style={{
          width: 620, maxWidth: '95vw',
          border: '2px solid var(--room-border, #5a4a1d)',
          padding: '18px 22px', boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
          fontFamily: 'Georgia, "Times New Roman", serif',
        }}
      >
        <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 20, letterSpacing: 2 }}>
          📼 Scene Recap
        </div>
        <div style={{ textAlign: 'center', fontSize: 11, opacity: 0.8, marginTop: 3 }}>
          Chronicler summary of the last {lineCount} lines in <b>{room}</b>.
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 12 }}>Lines to summarize:</label>
          <select
            value={lineCount}
            onChange={(e) => setLineCount(parseInt(e.target.value, 10))}
            disabled={busy}
            style={{ padding: '2px 6px', fontSize: 12 }}
            data-testid="scene-recap-lines"
          >
            {[15, 30, 50, 80].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <button
            data-testid="scene-recap-regenerate"
            onClick={generate}
            disabled={busy}
            style={{
              marginLeft: 'auto', padding: '3px 12px',
              background: '#5a4a1d', color: '#f5e6c0',
              border: '1px solid #3a2b14', fontWeight: 'bold',
              cursor: busy ? 'wait' : 'pointer', fontSize: 11,
            }}
          >{busy ? 'Working…' : 'Regenerate'}</button>
        </div>

        {err && (
          <div style={{ color: '#7a1f1f', marginTop: 8, fontSize: 12 }}>{err}</div>
        )}
        {!err && (
          <div
            data-testid="scene-recap-body"
            style={{
              marginTop: 12, padding: '10px 14px',
              background: 'var(--room-bg, #fafaf7)',
              border: '1px solid var(--room-border, #c8c8b8)',
              minHeight: 140, maxHeight: 360, overflowY: 'auto',
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap',
            }}
          >
            {busy && !summary ? 'Consulting the Chronicler…' : (summary || 'No recap yet.')}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button
            data-testid="scene-recap-close"
            onClick={onClose}
            style={{ padding: '4px 14px', background: '#5a5a5a', color: '#f5f5f0',
                     border: '1px solid #3a3a3a', fontWeight: 'bold', cursor: 'pointer' }}
          >Close</button>
          <button
            data-testid="scene-recap-save"
            onClick={save}
            disabled={busy || !summary || saved}
            style={{
              padding: '4px 14px',
              background: saved ? '#5a5a5a' : '#2d6b1e',
              color: '#f5faec',
              border: '1px solid #1a4a10',
              fontWeight: 'bold',
              cursor: (busy || !summary || saved) ? 'not-allowed' : 'pointer',
              opacity: (busy || !summary || saved) ? 0.6 : 1,
            }}
          >{saved ? 'SAVED ✓' : 'Save to Memory'}</button>
        </div>
      </div>
    </div>
  );
}
